import { useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';

export const useSSE = () => {
  const { user, token, fetchNotifications, refreshUser } = useAuth();
  const { t, language } = useLanguage();
  
  const eventSourceRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const reconnectDelayRef = useRef(1500); // grows on repeated failures, resets on a successful open
  const processedEventIdsRef = useRef(new Set());
  const lastEventIdRef = useRef(null);
  
  // Use a ref for the user object to avoid stale closures inside event listeners
  // while preventing reconnect loops when the user reference updates (e.g. balance changes)
  const userRef = useRef(user);
  useEffect(() => {
    userRef.current = user;
  }, [user]);

  // Request notification permission on token initialization
  useEffect(() => {
    if (token && 'Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission().catch(err => {
        console.error('Error requesting notification permission:', err);
      });
    }
  }, [token]);

  useEffect(() => {
    const userId = user?.id;
    const userConfirmed = user?.is_confirmed;
    const userAdmin = user?.is_admin;

    // Only connect if we have a token and user is confirmed or admin
    if (!token || !userId || (!userConfirmed && !userAdmin)) {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
      return;
    }
    
    const connect = () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }

      if (eventSourceRef.current) {
        eventSourceRef.current.close();
      }

      const baseUrl = '/api';

      // We append the token and lastEventId as a query param because EventSource doesn't support custom headers
      const url = `${baseUrl}/events?token=${token}${lastEventIdRef.current ? `&last_event_id=${encodeURIComponent(lastEventIdRef.current)}` : ''}`;
      
      const es = new EventSource(url);

      // Connection established -> reset the backoff so the next reconnect is fast.
      es.onopen = () => {
        reconnectDelayRef.current = 1500;
      };

      es.addEventListener('market_created', (e) => {
        if (e.lastEventId) {
          lastEventIdRef.current = e.lastEventId;
        }

        const data = JSON.parse(e.data);
        const eventKey = `market_${data.id}`;

        // Deduplicate
        if (processedEventIdsRef.current.has(eventKey)) {
          return;
        }
        processedEventIdsRef.current.add(eventKey);

        const currentUser = userRef.current;
        if (!currentUser) return;

        // Skip if the current user is the creator
        if (data.creator_id === currentUser.id || data.creator_username === currentUser.username) {
          return;
        }

        toast.info(language === 'de' ? `Neue Wette: ${data.title}` : `New Market: ${data.title}`, {
          description: language === 'de' ? `Erstellt von @${data.creator_username}` : `Created by @${data.creator_username}`,
          action: {
            label: t('viewAll'),
            onClick: () => window.location.href = `/market/${data.id}`
          }
        });
      });

      es.addEventListener('comment_added', (e) => {
        if (e.lastEventId) {
          lastEventIdRef.current = e.lastEventId;
        }

        const data = JSON.parse(e.data);
        const eventKey = `comment_${data.id}`;

        // Deduplicate
        if (processedEventIdsRef.current.has(eventKey)) {
          return;
        }
        processedEventIdsRef.current.add(eventKey);

        const currentUser = userRef.current;
        if (!currentUser) return;

        // Skip if current user wrote the comment
        if (data.username === currentUser.username) {
          return;
        }

        // Filter: Only notify if user is creator of the market, is mentioned, or has bet on it
        const isCreator = data.creator_id === currentUser.id;
        const isMentioned = data.comment.toLowerCase().includes(`@${currentUser.username.toLowerCase()}`);
        const hasBet = data.user_has_bet === true;

        if (!isCreator && !isMentioned && !hasBet) {
          return;
        }

        // We might not want to toast every comment if on the same page
        if (!window.location.pathname.includes(data.market_id)) {
          toast.message(language === 'de' ? `Neuer Kommentar von @${data.username}` : `New comment from @${data.username}`, {
            description: data.comment.length > 50 ? data.comment.substring(0, 50) + '...' : data.comment,
            action: {
              label: language === 'de' ? 'Ansehen' : 'View',
              onClick: () => window.location.href = `/market/${data.market_id}`
            }
          });
        }
      });

      es.addEventListener('market_updated', (e) => {
        const data = JSON.parse(e.data);
        const event = new CustomEvent('market_updated', { detail: data });
        window.dispatchEvent(event);
      });

      es.addEventListener('notification', (e) => {
        if (e.lastEventId) {
          lastEventIdRef.current = e.lastEventId;
        }

        const data = JSON.parse(e.data);
        const eventKey = `notif_${data.id}`;

        // Deduplicate
        if (processedEventIdsRef.current.has(eventKey)) {
          return;
        }
        processedEventIdsRef.current.add(eventKey);
        
        let url = null;
        if (data.market_id) {
          url = `/market/${data.market_id}`;
        } else if (data.type === 'friend_request') {
          url = '/friends';
        }

        const action = url ? {
          label: language === 'de' ? 'Ansehen' : 'View',
          onClick: () => window.location.href = url
        } : undefined;

        // Show toast
        toast.success(data.title, {
          description: data.message,
          action
        });

        // Show native notification if document is hidden and permission granted
        if (document.hidden && Notification.permission === 'granted') {
          navigator.serviceWorker.ready.then(registration => {
            registration.showNotification(data.title, {
              body: data.message,
              icon: '/logo-webapp.png',
              badge: '/logo-webapp.png',
              data: {
                url: url || '/'
              }
            });
          });
        } else if (Notification.permission === 'default') {
          // Optionally request permission if not already asked
          Notification.requestPermission();
        }

        fetchNotifications();
      });

      es.addEventListener('balance_update', (e) => {
        refreshUser();
      });

      es.onerror = (err) => {
        es.close();
        if (reconnectTimeoutRef.current) {
          clearTimeout(reconnectTimeoutRef.current);
        }
        // The backend ends the stream every ~25s (prod) on purpose, which also
        // fires onerror. Reconnect quickly so there's barely a gap; only back
        // off (capped) if the server is genuinely unreachable and never opens.
        const delay = reconnectDelayRef.current;
        reconnectDelayRef.current = Math.min(delay * 2, 15000);
        reconnectTimeoutRef.current = setTimeout(connect, delay);
      };

      eventSourceRef.current = es;
    };

    connect();

    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
    };
  }, [token, user?.id, user?.is_confirmed, user?.is_admin, language, fetchNotifications, refreshUser, t]);
};
